import BaseLayout from '@/layout/BaseLayout';

import { CampaignCard } from '@/components/CampaignCardVertical';
import { useHasScopes } from '@/hooks/useHasScopes';
import GridCampaignCard from '@/components/layout/GridCampaignCard';
import Loader from '@/components/Loader';
import { trpc } from '@/trpcClient';
import { ListCampaignFilterTypeWithValues } from '@shelflix/schemas/campaign/list';
import type { CampaignDto } from '@shelflix/trpc/routes/campaign/list/useCase';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { useEffect, useMemo, useRef, useState } from 'react';
import { DateRange } from 'react-day-picker';
import { useLocation, useNavigate } from 'react-router-dom';
import { toast } from 'sonner';
import { CampaignFilter } from './CampaignFilter';

export function CampaignList() {
  const location = useLocation();
  const navigate = useNavigate();
  const hasShownToast = useRef(false);
  const queryClient = useQueryClient();
  const [page, setPage] = useState(1);
  // const pageSize = 100;

  const [filters, setFilters] = useState<ListCampaignFilterTypeWithValues>({});
  const [period, setPeriod] = useState<DateRange | null>(null);

  const { data, isLoading, isError, isFetching } = useQuery(
    trpc.campaign.list.queryOptions({
      page,

      recordsPerPage: 9999,
      period: period
        ? {
            start: period.from?.toISOString(),
            end: period.to?.toISOString(),
          }
        : undefined,
      ...(filters.search && { search: { ...filters.search } }),
      ...(filters.filter && { filter: filters.filter.map((f) => ({ ...f.field })) }),
      ...(filters.sort && { sort: filters.sort }),
    })
  );

  const campaigns = data?.campaigns ?? [];
  const canEditCampaign = useHasScopes(['campaign:edit']);

  const handleEdit = (uuid: string): void => {
    if (canEditCampaign) {
      navigate(`edit/${uuid}`);
    }
  };

  useEffect(() => {
    if (location.state?.toastSuccess && !hasShownToast.current) {
      toast.success('Alteração salva com sucesso');
      hasShownToast.current = true;

      // Forçar refetch da lista de campanhas
      queryClient.invalidateQueries({ queryKey: ['campaign.list'] }).then(() => {
        navigate(location.pathname, { replace: true, state: {} });
      });
    }
  }, [location.state]);

  const groupedCampaigns = useMemo(
    () =>
      campaigns.reduce(
        (acc, material) => {
          if (!acc[material.brand.uuid]) {
            acc[material.brand.uuid] = [];
          }
          acc[material.brand.uuid].push(material);
          return acc;
        },
        {} as Record<string, CampaignDto[]>
      ),
    [campaigns]
  );

  function CampaignCardWrapper({ campaign }: { campaign: CampaignDto }) {
    return (
      <CampaignCard
        campaign={campaign}
        key={campaign.uuid}
        actions={{
          onClick: canEditCampaign ? () => handleEdit(campaign.uuid) : undefined,
        }}
      />
    );
  }

  return (
    <BaseLayout>
      <section className="flex w-full flex-col gap-8">
        <CampaignFilter
          onChange={(filters) => {
            setFilters(filters);
            setPage(1);
          }}
          period={period}
          onChangePeriod={(period) => {
            setPeriod(period);
            setPage(1);
          }}
        />
        <section className="grid w-full gap-3 px-2 sm:grid-cols-2 xl:grid-cols-3">
          {(isLoading || isFetching) && (
            <div className="col-span-full">
              <Loader message="Carregando campanhas..." />
            </div>
          )}
          {isError && <p className="p-4 text-red-500">Erro ao carregar campanhas.</p>}
          {!isLoading &&
            !isFetching &&
            !isError &&
            Object.entries(groupedCampaigns)
              .sort((a, b) => a[1][0].brand.name.localeCompare(b[1][0].brand.name))
              .map(([brandUid, campaigns]) => (
                <div key={brandUid} className="col-span-1 bg-[var(--popover)]/60 px-2 pb-6">
                  <div className="mt-3 mb-2 ml-2 flex items-center gap-2 text-xl font-semibold">
                    <div className="h-6 w-2 rounded-full bg-[var(--primary)]"></div>
                    <h3 className="text-md leading-5 font-semibold sm:text-xl">{campaigns[0].brand.name}</h3>
                  </div>

                  {/* Grid para desktop/tablet */}
                  <GridCampaignCard>
                    {campaigns.map((campaign) => (
                      <CampaignCardWrapper key={campaign.uuid} campaign={campaign} />
                    ))}
                  </GridCampaignCard>
                </div>
              ))}
        </section>
      </section>
    </BaseLayout>
  );
}

// <>
//   <h2 className="col-span-full mt-6 flex items-center gap-2 text-xl font-semibold">
//     <div className="h-6 w-2 rounded-full bg-orange-500"></div>
//     {campaigns[0].brand.name}
//     <span className="text-sm font-normal text-gray-500">
//       ({campaigns.length} materi{campaigns.length > 1 ? 'ais' : 'al'})
//     </span>
//   </h2>
//   <GridCampaignCard key={brandUid} className="mt-4 gap-4">
//     {campaigns.map((campaign) => (
//       <CampaignCard
//         campaign={campaign}
//         key={campaign.uuid}
//         onInfo={() => handleEdit(campaign.uuid)}
//         actions={{
//           onEdit: () => handleEdit(campaign.uuid),
//         }}
//       />
//     ))}
//   </GridCampaignCard>
// </>
